import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { hostApi, Host } from '../../services/api/host.api';

export default function HostsPage() {
  const { t } = useTranslation(['infrastructure', 'common']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hosts, setHosts] = useState<Host[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalHosts, setTotalHosts] = useState(0);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * pageSize;
      const result = await hostApi.getHosts(pageSize, offset);
      setHosts(result.data);
      setTotalHosts(result.meta.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load hosts');
      console.error('Hosts fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'offline':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getProgressColor = (value: number) => {
    if (value >= 90) return 'bg-red-500';
    if (value >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('infrastructure:hosts.loading')}</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('infrastructure:hosts.title')}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t('infrastructure:hosts.subtitle')}
          </p>
        </div>
        <button
          onClick={() => navigate('/agents/host/install')}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
        >
          <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t('infrastructure:hosts.setupAgent')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('infrastructure:hosts.totalHosts')}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{totalHosts}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('infrastructure:hosts.online')}</p>
          <p className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
            {hosts.filter((h) => h.status === 'online').length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('infrastructure:hosts.offline')}</p>
          <p className="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">
            {hosts.filter((h) => h.status === 'offline').length}
          </p>
        </div>
      </div>

      {/* Hosts Grid */}
      {hosts.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-900">
          <div className="mx-auto max-w-md">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/20">
              <svg className="h-8 w-8 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t('infrastructure:hosts.noHostsConnected')}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('infrastructure:hosts.noHostsDescription')}
            </p>
            <button
              onClick={() => navigate('/agents/host/install')}
              className="mt-6 inline-flex items-center rounded-lg bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('infrastructure:hosts.installHostAgent')}
            </button>
            <button
              onClick={() => navigate('/agents')}
              className="ml-3 mt-6 inline-flex items-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {t('infrastructure:hosts.viewAllAgents')}
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {hosts.map((host) => (
            <div
              key={host.id}
              onClick={() => navigate(`/infrastructure/hosts/${host.id}`)}
              className="cursor-pointer rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:border-indigo-500 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-indigo-500"
            >
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {host.hostname}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{host.ip_address}</p>
                  {host.os && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                      {host.os} {host.os_version && `${host.os_version}`}
                    </p>
                  )}
                </div>
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(host.status)}`}>
                  {host.status}
                </span>
              </div>

              {/* CPU */}
              {host.metadata?.cpu_usage !== undefined && (
                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('infrastructure:hosts.cpu')}
                    </span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {host.metadata.cpu_usage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className={`h-full ${getProgressColor(host.metadata.cpu_usage)}`}
                      style={{ width: `${Math.min(100, host.metadata.cpu_usage)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Memory */}
              {host.metadata?.memory_usage !== undefined && (
                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('infrastructure:hosts.memory')}
                    </span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {host.metadata.memory_usage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className={`h-full ${getProgressColor(host.metadata.memory_usage)}`}
                      style={{ width: `${Math.min(100, host.metadata.memory_usage)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Disk */}
              {host.metadata?.disk_usage !== undefined && (
                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('infrastructure:hosts.disk')}
                    </span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {host.metadata.disk_usage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className={`h-full ${getProgressColor(host.metadata.disk_usage)}`}
                      style={{ width: `${Math.min(100, host.metadata.disk_usage)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Network & Uptime */}
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
                {(host.metadata?.network_in !== undefined || host.metadata?.network_out !== undefined) && (
                  <>
                    {host.metadata?.network_in !== undefined && (
                      <div className="text-xs">
                        <span className="text-gray-500 dark:text-gray-500">↓ </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {host.metadata.network_in.toFixed(1)} MB/s
                        </span>
                      </div>
                    )}
                    {host.metadata?.network_out !== undefined && (
                      <div className="text-xs">
                        <span className="text-gray-500 dark:text-gray-500">↑ </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {host.metadata.network_out.toFixed(1)} MB/s
                        </span>
                      </div>
                    )}
                  </>
                )}
                {host.metadata?.uptime && (
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    {t('infrastructure:hosts.uptime')}: {host.metadata.uptime}
                  </div>
                )}
                {host.last_seen_at && (
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    {t('infrastructure:hosts.lastSeen')}: {new Date(host.last_seen_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {hosts.length > 0 && (
        <div className="mt-8 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('infrastructure:pagination.showing')} {((currentPage - 1) * pageSize) + 1} {t('infrastructure:pagination.to')} {Math.min(currentPage * pageSize, totalHosts)} {t('infrastructure:pagination.of')} {totalHosts} {t('infrastructure:pagination.totalHosts')}
            </span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="ml-4 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
            >
              <option value={10}>10 {t('infrastructure:pagination.perPage')}</option>
              <option value={20}>20 {t('infrastructure:pagination.perPage')}</option>
              <option value={50}>50 {t('infrastructure:pagination.perPage')}</option>
            </select>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {t('infrastructure:pagination.first')}
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {t('infrastructure:pagination.previous')}
            </button>
            <span className="flex items-center px-3 text-sm text-gray-700 dark:text-gray-300">
              {t('infrastructure:pagination.page')} {currentPage} {t('infrastructure:pagination.of')} {Math.ceil(totalHosts / pageSize)}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= Math.ceil(totalHosts / pageSize)}
              className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {t('infrastructure:pagination.next')}
            </button>
            <button
              onClick={() => setCurrentPage(Math.ceil(totalHosts / pageSize))}
              disabled={currentPage >= Math.ceil(totalHosts / pageSize)}
              className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {t('infrastructure:pagination.last')}
            </button>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
